import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Calendar, CheckCircle, Users, DollarSign, XCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface ProviderStatsCardsProps {
  stats: {
    totalBookings: number;
    confirmedBookings: number;
    completedBookings: number;
    totalRevenue: number;
    cancelledBookings: number;
  } | null;
  isLoading: boolean;
}

const ProviderStatsCards = ({ stats, isLoading }: ProviderStatsCardsProps) => {
  const isMobile = useIsMobile();

  if (isLoading) {
    const loadingCards = [...Array(5)].map((_, i) => (
      <Card key={i} className="animate-pulse min-w-0">
        <CardHeader className="pb-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </CardHeader>
        <CardContent>
          <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-full"></div>
        </CardContent>
      </Card>
    ));

    if (isMobile) {
      return (
        <div className="mb-8">
          <Carousel className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {loadingCards.map((card, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2">
                  {card}
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {loadingCards}
      </div>
    );
  }

  if (!stats) return null;

  const statsData = [
    {
      title: "Total Bookings",
      value: stats.totalBookings,
      description: "All time bookings",
      Icon: Calendar,
      color: "text-blue-600"
    },
    {
      title: "Upcoming",
      value: stats.confirmedBookings,
      description: "Active bookings",
      Icon: Users,
      color: "text-green-600"
    },
    {
      title: "Completed",
      value: stats.completedBookings,
      description: "Finished sessions",
      Icon: CheckCircle,
      color: "text-emerald-600"
    },
    {
      title: "Total Revenue",
      value: `£${stats.totalRevenue.toFixed(2)}`,
      description: "All time earnings",
      Icon: DollarSign,
      color: "text-yellow-600"
    },
    {
      title: "Cancelled",
      value: stats.cancelledBookings,
      description: "Cancelled bookings",
      Icon: XCircle,
      color: "text-red-600"
    }
  ];

  const statCards = statsData.map((stat) => (
    <Card key={stat.title} className="min-w-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
        <stat.Icon className={`h-4 w-4 ${stat.color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{stat.value}</div>
        <p className="text-xs text-muted-foreground">
          {stat.description}
        </p>
      </CardContent>
    </Card>
  ));

  // Mobile: Use carousel
  if (isMobile) {
    return (
      <div className="mb-8">
        <Carousel className="w-full">
          <CarouselContent className="-ml-2 md:-ml-4">
            {statCards.map((card, index) => (
              <CarouselItem key={index} className="pl-2 md:pl-4 basis-4/5 sm:basis-1/2">
                {card}
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2" />
          <CarouselNext className="right-2" />
        </Carousel>
      </div>
    );
  }

  // Desktop: Use grid
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      {statCards}
    </div>
  );
};

export default ProviderStatsCards;
